import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type PropsWithChildren,
} from 'react';
import type { ILogging } from '../api/types/ILogging';
import { useInstanceId } from '../hooks/useInstanceId';
import { bridge } from '../utils';

export type AnalyticsContextValue = {
  getLogging: () => ILogging;
};

type AnalyticsProviderProps = PropsWithChildren<{
  screen: string;
  getDefaults?: () => ILogging | undefined;
}>;

const AnalyticsContext = createContext<AnalyticsContextValue>({
  getLogging: () => ({}),
});

export const AnalyticsProvider = ({
  screen,
  getDefaults,
  children,
}: AnalyticsProviderProps) => {
  const instanceId = useInstanceId();

  useEffect(() => {
    bridge.setScreenName({ instanceId, name: screen });
  }, [instanceId, screen]);

  const getLogging = useCallback((): ILogging => {
    const baseDefaults = getDefaults?.();
    const effectiveDefaults: ILogging = {
      ...baseDefaults,
      analytics: { SCREEN: screen, ...baseDefaults?.analytics },
    };
    return effectiveDefaults;
  }, [screen, getDefaults]);

  const value = useMemo(() => ({ getLogging }), [getLogging]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalyticsContext = (): AnalyticsContextValue =>
  useContext(AnalyticsContext);
